import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  Card,
  Badge,
  Table,
  Button,
  Tabs,
  StatCard,
  Spinner,
  EmptyState,
  QueryError,
} from "../components";
import SeverityBadge from "../components/SeverityBadge";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPost } from "../lib/api";
import { useToast } from "../contexts/ToastContext";

interface Prescription {
  action: string;
  label: string;
  description: string;
  automated: boolean;
  endpoint?: string;
}

interface Alert {
  id: string;
  deploymentId?: string;
  tenantId?: string;
  category: string;
  severity: string;
  title: string;
  message: string;
  prescriptions: Prescription[];
  status: string;
  acknowledgedBy?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface AlertSummary {
  critical: number;
  warning: number;
  info: number;
  total: number;
  open: number;
  acknowledged: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  health: "Health",
  cost: "Cost",
  learning: "Learning",
  compliance: "Compliance",
};

const STATUS_COLORS: Record<string, "red" | "amber" | "blue" | "green" | "gray"> = {
  open: "red",
  acknowledged: "amber",
  resolved: "green",
  dismissed: "gray",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function ActionCenter() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("active");
  const [categoryFilter, setCategoryFilter] = useState<string | "">("");

  const statusFilter =
    activeTab === "active" ? "open" : activeTab === "acknowledged" ? "acknowledged" : "";
  const queryParams = new URLSearchParams();
  if (statusFilter) queryParams.set("status", statusFilter);
  if (categoryFilter) queryParams.set("category", categoryFilter);
  queryParams.set("limit", "100");

  const {
    data: alertsData,
    isLoading,
    error,
  } = useQuery<{ alerts: Alert[]; total: number }>({
    queryKey: ["alerts", activeTab, categoryFilter],
    queryFn: () => apiGet(`/api/alerts?${queryParams.toString()}`),
  });

  const { data: summary } = useQuery<AlertSummary>({
    queryKey: ["alerts-summary"],
    queryFn: () => apiGet("/api/alerts/summary"),
  });

  usePolling([["alerts", activeTab, categoryFilter], ["alerts-summary"]], 30_000);

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/api/alerts/${id}/acknowledge`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      queryClient.invalidateQueries({ queryKey: ["alerts-summary"] });
      toast.success("Alert acknowledged");
    },
    onError: (err: Error) => toast.error("Failed to acknowledge", err.message),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/api/alerts/${id}/resolve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      queryClient.invalidateQueries({ queryKey: ["alerts-summary"] });
      toast.success("Alert resolved");
    },
    onError: (err: Error) => toast.error("Failed to resolve", err.message),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/api/alerts/${id}/dismiss`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      queryClient.invalidateQueries({ queryKey: ["alerts-summary"] });
      toast.success("Alert dismissed");
    },
    onError: (err: Error) => toast.error("Failed to dismiss", err.message),
  });

  const executeActionMutation = useMutation({
    mutationFn: ({ id, actionIndex }: { id: string; actionIndex: number }) =>
      apiPost(`/api/alerts/${id}/execute-action`, { actionIndex }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      queryClient.invalidateQueries({ queryKey: ["alerts-summary"] });
      toast.success("Action executed successfully");
    },
    onError: (err: Error) => toast.error("Action failed", err.message),
  });

  const alerts = alertsData?.alerts ?? [];
  const visibleSeverity = alerts.reduce(
    (acc, alert) => {
      if (alert.severity === "critical") acc.critical += 1;
      else if (alert.severity === "warning") acc.warning += 1;
      else if (alert.severity === "info") acc.info += 1;
      return acc;
    },
    { critical: 0, warning: 0, info: 0 },
  );

  if (isLoading && !alertsData) {
    return (
      <div>
        <PageHeader title="Action Center" subtitle="Proactive alerts and recommended actions" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !alertsData) {
    return (
      <div>
        <PageHeader title="Action Center" subtitle="Proactive alerts and recommended actions" />
        <QueryError error={error} queryKeys={[["alerts"]]} />
      </div>
    );
  }

  const tabs = [
    { id: "active", label: `Active (${summary?.open ?? 0})` },
    { id: "acknowledged", label: `Acknowledged (${summary?.acknowledged ?? 0})` },
    { id: "all", label: "All Alerts" },
  ];

  return (
    <div>
      <PageHeader title="Action Center" subtitle="Proactive alerts and recommended actions" />

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          title="Critical"
          value={String(visibleSeverity.critical)}
          color={visibleSeverity.critical ? "var(--c-danger)" : undefined}
        />
        <StatCard
          title="Warning"
          value={String(visibleSeverity.warning)}
          color={visibleSeverity.warning ? "var(--c-warning)" : undefined}
        />
        <StatCard title="Info" value={String(visibleSeverity.info)} />
      </div>

      {/* Tabs + filter */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid var(--c-border)",
            background: "var(--c-surface)",
            color: "var(--c-text)",
            fontSize: 13,
          }}
        >
          <option value="">All categories</option>
          <option value="health">Health</option>
          <option value="cost">Cost</option>
          <option value="learning">Learning</option>
          <option value="compliance">Compliance</option>
        </select>
      </div>

      {/* Alert list */}
      {alerts.length === 0 ? (
        <EmptyState
          title={activeTab === "active" ? "No active alerts" : "No alerts found"}
          description="All systems are operating normally."
        />
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <Card key={alert.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <SeverityBadge severity={alert.severity} />
                    <Badge color="gray">{CATEGORY_LABELS[alert.category] ?? alert.category}</Badge>
                    {alert.status !== "open" && (
                      <Badge color={STATUS_COLORS[alert.status]}>{alert.status}</Badge>
                    )}
                    <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                      {timeAgo(alert.createdAt)}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold mb-1" style={{ color: "var(--c-text)" }}>
                    {alert.title}
                  </h3>
                  <p className="text-sm mb-2" style={{ color: "var(--c-text-secondary)" }}>
                    {alert.message}
                  </p>

                  {alert.deploymentId && (
                    <Link
                      to={`/deployments/${alert.deploymentId}`}
                      className="text-xs"
                      style={{ color: "var(--c-primary)" }}
                    >
                      View deployment
                    </Link>
                  )}

                  {/* Prescriptions */}
                  {alert.prescriptions.length > 0 && (
                    <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--c-border)" }}>
                      <div
                        className="text-xs font-medium mb-2"
                        style={{ color: "var(--c-text-secondary)" }}
                      >
                        Recommended actions:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {alert.prescriptions.map((p, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              if (p.automated) {
                                executeActionMutation.mutate({
                                  id: alert.id,
                                  actionIndex: i,
                                });
                              }
                            }}
                            title={p.description}
                            style={{
                              padding: "4px 10px",
                              fontSize: 12,
                              borderRadius: 4,
                              border: "1px solid var(--c-border)",
                              background: p.automated ? "var(--c-primary)" : "var(--c-surface-2)",
                              color: p.automated ? "#fff" : "var(--c-text-secondary)",
                              cursor: p.automated ? "pointer" : "default",
                              opacity: p.automated ? 1 : 0.7,
                            }}
                            disabled={!p.automated || executeActionMutation.isPending}
                          >
                            {p.label}
                            {!p.automated && (
                              <span style={{ marginLeft: 4, fontSize: 10 }}>(manual)</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                {(alert.status === "open" || alert.status === "acknowledged") && (
                  <div className="flex flex-col gap-1 shrink-0">
                    {alert.status === "open" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => acknowledgeMutation.mutate(alert.id)}
                        disabled={acknowledgeMutation.isPending}
                      >
                        Acknowledge
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => resolveMutation.mutate(alert.id)}
                      disabled={resolveMutation.isPending}
                    >
                      Resolve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismissMutation.mutate(alert.id)}
                      disabled={dismissMutation.isPending}
                    >
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
